import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Fish } from '@phosphor-icons/react';

const FishSpeciesPage = () => {
  const { language } = useLanguage();

  const titles = {
    fr: "Une belle variété d'espèces en Corse",
    en: "A beautiful variety of species in Corsica",
    it: "Una bella varietà di specie in Corsica"
  };

  const descriptions = {
    fr: "Les eaux de Corse, riches et préservées, abritent une grande diversité de poissons. Ces espèces varient selon les saisons, les conditions météorologiques et les techniques de pêche utilisées. Avec Calvi Fishing, partez à la découverte de ces poissons dans les eaux cristallines de Corse !",
    en: "The waters of Corsica, rich and preserved, are home to a great diversity of fish. These species vary according to seasons, weather conditions and fishing techniques used. With Calvi Fishing, discover these fish in the crystal-clear waters of Corsica!",
    it: "Le acque della Corsica, ricche e preservate, ospitano una grande diversità di pesci. Queste specie variano secondo le stagioni, le condizioni meteorologiche e le tecniche di pesca utilizzate. Con Calvi Fishing, scopri questi pesci nelle acque cristalline della Corsica!"
  };

  const categories = {
    fr: {
      coastal: "Poissons côtiers",
      pelagic: "Poissons pélagiques",
      other: "Autres espèces notables"
    },
    en: {
      coastal: "Coastal Fish",
      pelagic: "Pelagic Fish",
      other: "Other Notable Species"
    },
    it: {
      coastal: "Pesci costieri",
      pelagic: "Pesci pelagici",
      other: "Altre specie notevoli"
    }
  };

  const fishSpecies = [
    {
      id: 1,
      name: { fr: "Sars", en: "Sea Bream", it: "Sarago" },
      category: "coastal",
      image: "https://static.wixstatic.com/media/ce6ce7_b708baf7b04246b3888325d1fa18f546~mv2.jpg/v1/fill/w_459,h_459,fp_0.31_0.33,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/sar.jpg",
      description: {
        fr: "Poisson emblématique des côtes corses, le sar est apprécié pour sa combativité et sa chair savoureuse. Vivant près des rochers et des fonds sableux, il offre une pêche technique et passionnante.",
        en: "An emblematic fish of the Corsican coasts, the sea bream is appreciated for its fighting spirit and tasty flesh. Living near rocks and sandy bottoms, it offers technical and exciting fishing.",
        it: "Pesce emblematico delle coste corse, il sarago è apprezzato per la sua combattività e la sua carne saporita. Vivendo vicino alle rocce e ai fondali sabbiosi, offre una pesca tecnica e appassionante."
      }
    },
    {
      id: 2,
      name: { fr: "Pageots", en: "Pandora", it: "Pagello" },
      category: "coastal",
      image: "https://static.wixstatic.com/media/ce6ce7_b7a31d41ba6f440ca2d6b70343626d9e~mv2.jpg/v1/fill/w_459,h_459,fp_0.61_0.42,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/PAGEOT.jpg",
      description: {
        fr: "Reconnaissable à sa teinte rosée, le pageot est un poisson prisé des pêcheurs pour sa chair fine et savoureuse. Il évolue sur les fonds sableux et rocheux, offrant une pêche accessible et agréable.",
        en: "Recognizable by its pinkish hue, the pandora is a fish prized by fishermen for its fine and tasty flesh. It lives on sandy and rocky bottoms, offering accessible and pleasant fishing.",
        it: "Riconoscibile per la sua tonalità rosata, il pagello è un pesce apprezzato dai pescatori per la sua carne fine e saporita. Vive su fondali sabbiosi e rocciosi, offrendo una pesca accessibile e piacevole."
      }
    },
    {
      id: 3,
      name: { fr: "Pagres", en: "Red Porgy", it: "Pagro" },
      category: "coastal",
      image: "https://customer-assets.emergentagent.com/job_83099e1e-aac8-4c9e-af23-c07649c0310f/artifacts/fwjempzi_Pagre.png",
      focusPoint: "40% 60%",
      description: {
        fr: "Puissant et méfiant, le pagre est un poisson recherché pour sa combativité et sa chair savoureuse. Il évolue sur les fonds rocheux et sableux, offrant une pêche sportive et pleine de sensations.",
        en: "Powerful and wary, the red porgy is a fish sought for its fighting spirit and tasty flesh. It lives on rocky and sandy bottoms, offering sporty fishing full of sensations.",
        it: "Potente e diffidente, il pagro è un pesce ricercato per la sua combattività e la sua carne saporita. Vive su fondali rocciosi e sabbiosi, offrendo una pesca sportiva e piena di sensazioni."
      }
    },
    {
      id: 4,
      name: { fr: "Chapons", en: "Scorpionfish", it: "Scorfano" },
      category: "coastal",
      image: "https://static.wixstatic.com/media/ce6ce7_5e7ced3f879f448f96e80e5843522cd2~mv2.jpeg/v1/fill/w_459,h_459,fp_0.63_0.49,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/RASCASSE.jpeg",
      description: {
        fr: "Avec sa couleur rouge intense et ses épines impressionnantes, le chapon est un poisson emblématique de Méditerranée. Caché sur les fonds rocheux, il se pêche avec patience et offre une chair délicieuse, prisée en gastronomie.",
        en: "With its intense red color and impressive spines, the scorpionfish is an emblematic fish of the Mediterranean. Hidden on rocky bottoms, it is caught with patience and offers delicious flesh, prized in gastronomy.",
        it: "Con il suo colore rosso intenso e le sue spine impressionanti, lo scorfano è un pesce emblematico del Mediterraneo. Nascosto sui fondali rocciosi, si pesca con pazienza e offre una carne deliziosa, apprezzata in gastronomia."
      }
    },
    {
      id: 5,
      name: { fr: "Denti", en: "Dentex", it: "Dentice" },
      category: "coastal",
      image: "https://static.wixstatic.com/media/ce6ce7_395530e9a68d442b9348e0af44a86278~mv2.jpg/v1/fill/w_459,h_459,fp_0.65_0.42,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Denti.jpg",
      description: {
        fr: "Poisson emblématique de Méditerranée, le denti est un chasseur redoutable, apprécié pour sa combativité et sa chair savoureuse. Il évolue près des reliefs sous-marins et offre une pêche sportive pleine d'adrénaline.",
        en: "An emblematic fish of the Mediterranean, the dentex is a formidable hunter, appreciated for its fighting spirit and tasty flesh. It lives near underwater reliefs and offers sporty fishing full of adrenaline.",
        it: "Pesce emblematico del Mediterraneo, il dentice è un cacciatore formidabile, apprezzato per la sua combattività e la sua carne saporita. Vive vicino ai rilievi sottomarini e offre una pesca sportiva piena di adrenalina."
      }
    },
    {
      id: 6,
      name: { fr: "Dorade royale", en: "Gilthead Bream", it: "Orata" },
      category: "coastal",
      image: "https://static.wixstatic.com/media/ce6ce7_a08ec698b2c246938d59d165bbc1fa79~mv2.png/v1/fill/w_439,h_439,fp_0.30_0.55,lg_1,q_85,enc_avif,quality_auto/Capture%20d%E2%80%99%C3%A9cran%202025-03-25%20%C3%A0%2017_27_25.png",
      description: {
        fr: "Reconnaissable à son front doré, la dorade royale est un poisson recherché pour sa chair fine et délicieuse. Vive et méfiante, elle évolue sur les fonds sableux et rocheux, offrant une pêche technique et passionnante.",
        en: "Recognizable by its golden forehead, the gilthead bream is a fish sought for its fine and delicious flesh. Lively and wary, it lives on sandy and rocky bottoms, offering technical and exciting fishing.",
        it: "Riconoscibile per la sua fronte dorata, l'orata è un pesce ricercato per la sua carne fine e deliziosa. Vivace e diffidente, vive su fondali sabbiosi e rocciosi, offrendo una pesca tecnica e appassionante."
      }
    },
    {
      id: 7,
      name: { fr: "Sériole", en: "Greater Amberjack", it: "Ricciola" },
      category: "pelagic",
      image: "https://static.wixstatic.com/media/ce6ce7_d06e8109c7d54d68820a7ab23dc68412~mv2.jpg/v1/fill/w_459,h_300,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/seriole.jpg",
      description: {
        fr: "Rapide et combattive, la sériole est l'un des poissons les plus prisés en pêche sportive. Ce prédateur majestueux évolue en bancs près des épaves et des reliefs sous-marins, offrant des combats intenses et mémorables.",
        en: "Fast and combative, the greater amberjack is one of the most prized fish in sport fishing. This majestic predator lives in schools near wrecks and underwater reliefs, offering intense and memorable fights.",
        it: "Veloce e combattiva, la ricciola è uno dei pesci più apprezzati nella pesca sportiva. Questo maestoso predatore vive in banchi vicino ai relitti e ai rilievi sottomarini, offrendo combattimenti intensi e memorabili."
      }
    },
    {
      id: 8,
      name: { fr: "Thon rouge", en: "Bluefin Tuna", it: "Tonno rosso" },
      category: "pelagic",
      image: "https://customer-assets.emergentagent.com/job_83099e1e-aac8-4c9e-af23-c07649c0310f/artifacts/3ocyz0zz_THON.png",
      focusPoint: "50% 25%",
      description: {
        fr: "Puissant et endurant, le thon rouge est l'un des poissons les plus emblématiques de la pêche sportive. Ce géant des mers offre des combats spectaculaires et une montée d'adrénaline inoubliable.",
        en: "Powerful and enduring, the bluefin tuna is one of the most emblematic fish of sport fishing. This giant of the seas offers spectacular fights and an unforgettable adrenaline rush.",
        it: "Potente e resistente, il tonno rosso è uno dei pesci più emblematici della pesca sportiva. Questo gigante dei mari offre combattimenti spettacolari e una scarica di adrenalina indimenticabile."
      }
    },
    {
      id: 9,
      name: { fr: "Bonite", en: "Bonito", it: "Bonito" },
      category: "pelagic",
      image: "https://static.wixstatic.com/media/ce6ce7_a51d9a9d04054e7b92ea04885613903c~mv2.jpg/v1/fill/w_459,h_300,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/BONITE.jpg",
      description: {
        fr: "Rapide et nerveuse, la bonite est un petit thonidé qui offre des combats explosifs. Elle évolue en bancs à la surface et se pêche avec des techniques dynamiques, idéales pour des sensations fortes.",
        en: "Fast and nervous, the bonito is a small tuna that offers explosive fights. It lives in schools on the surface and is caught with dynamic techniques, ideal for thrills.",
        it: "Veloce e nervoso, il bonito è un piccolo tonno che offre combattimenti esplosivi. Vive in banchi in superficie e si pesca con tecniche dinamiche, ideali per emozioni forti."
      }
    },
    {
      id: 12,
      name: { fr: "Barracuda", en: "Barracuda", it: "Barracuda" },
      category: "pelagic",
      image: "https://customer-assets.emergentagent.com/job_83099e1e-aac8-4c9e-af23-c07649c0310f/artifacts/w6kncq0j_BARRACUDA.png",
      description: {
        fr: "Avec son corps fuselé et ses dents acérées, le barracuda méditerranéen est un prédateur impressionnant. Rapide et agressif, il chasse près des côtes et offre une pêche pleine d'adrénaline.",
        en: "With its streamlined body and sharp teeth, the Mediterranean barracuda is an impressive predator. Fast and aggressive, it hunts near the coasts and offers fishing full of adrenaline.",
        it: "Con il suo corpo affusolato e i suoi denti affilati, il barracuda mediterraneo è un predatore impressionante. Veloce e aggressivo, caccia vicino alle coste e offre una pesca piena di adrenalina."
      }
    },
    {
      id: 13,
      name: { fr: "Mostelle", en: "Forkbeard", it: "Musdea" },
      category: "other",
      image: "https://static.wixstatic.com/media/ce6ce7_bce45d28eaa54691a76acd0a45bf8146~mv2.jpg/v1/fill/w_459,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/MUSTELLE.jpg",
      description: {
        fr: "Discrète et allongée, la mostelle évolue sur les fonds rocheux et sableux de Méditerranée. Appréciée pour sa chair fine, elle se pêche avec des techniques spécifiques, offrant une expérience intéressante aux passionnés.",
        en: "Discreet and elongated, the forkbeard lives on rocky and sandy bottoms of the Mediterranean. Appreciated for its fine flesh, it is caught with specific techniques, offering an interesting experience to enthusiasts.",
        it: "Discreta e allungata, la musdea vive sui fondali rocciosi e sabbiosi del Mediterraneo. Apprezzata per la sua carne fine, si pesca con tecniche specifiche, offrendo un'esperienza interessante agli appassionati."
      }
    },
    {
      id: 14,
      name: { fr: "Congre", en: "Conger Eel", it: "Grongo" },
      category: "other",
      image: "https://static.wixstatic.com/media/ce6ce7_f055057be3ac4829b64b837a7b7fa988~mv2.jpg/v1/fill/w_459,h_340,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/CONGRE.jpg",
      description: {
        fr: "Avec son corps allongé et sa puissance impressionnante, le congre est un poisson nocturne qui se cache dans les failles rocheuses. Combatif et tenace, il offre une pêche intense et technique.",
        en: "With its elongated body and impressive power, the conger eel is a nocturnal fish that hides in rocky crevices. Combative and tenacious, it offers intense and technical fishing.",
        it: "Con il suo corpo allungato e la sua potenza impressionante, il grongo è un pesce notturno che si nasconde nelle fessure rocciose. Combattivo e tenace, offre una pesca intensa e tecnica."
      }
    },
    {
      id: 15,
      name: { fr: "Calamar", en: "Squid", it: "Calamaro" },
      category: "other",
      image: "https://static.wixstatic.com/media/ce6ce7_5107a82f74e3484e95c2536b20403290~mv2.png/v1/fill/w_459,h_260,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/CALAMAR.png",
      description: {
        fr: "Créature mystérieuse des eaux corses, le calamar se pêche de nuit à l'aide de techniques spécifiques. Attiré par la lumière, il offre une expérience captivante et un défi délicat pour les pêcheurs.",
        en: "A mysterious creature of Corsican waters, the squid is caught at night using specific techniques. Attracted by light, it offers a captivating experience and a delicate challenge for fishermen.",
        it: "Creatura misteriosa delle acque corse, il calamaro si pesca di notte con tecniche specifiche. Attratto dalla luce, offre un'esperienza accattivante e una sfida delicata per i pescatori."
      }
    }
  ];

  const coastalFish = fishSpecies.filter(f => f.category === 'coastal');
  const pelagicFish = fishSpecies.filter(f => f.category === 'pelagic');
  const otherFish = fishSpecies.filter(f => f.category === 'other');

  const FishCard = ({ fish }) => (
    <div className="bg-white rounded-2xl overflow-hidden card-shadow hover-lift">
      <div className="relative h-48 overflow-hidden">
        <img 
          src={fish.image} 
          alt={fish.name[language] || fish.name.fr}
          className="w-full h-full object-cover"
          style={fish.focusPoint ? { objectPosition: fish.focusPoint } : undefined}
        />
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold text-[#000000] font-['Playfair_Display'] mb-3">
          {fish.name[language] || fish.name.fr}
        </h3>
        <p className="text-[#4B5563] text-sm leading-relaxed">
          {fish.description[language] || fish.description.fr}
        </p>
      </div>
    </div>
  );

  const FishSection = ({ title, fish }) => (
    <div className="mb-16">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-full bg-[#b7ede0] flex items-center justify-center">
          <Fish size={24} className="text-[#000000]" />
        </div>
        <h2 className="text-2xl font-bold text-[#000000] font-['Playfair_Display']">
          {title}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {fish.map(f => (
          <FishCard key={f.id} fish={f} />
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pb-20 bg-[#000000]" data-testid="fish-species-page">
      {/* Header */}
      <div className="bg-[#000000] pt-28 sm:pt-36 pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-white font-['Playfair_Display'] mb-4">
            {titles[language] || titles.fr}
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl">
            {descriptions[language] || descriptions.fr}
          </p>
        </div>
      </div>

      {/* Fish Species */}
      <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <FishSection 
          title={categories[language]?.coastal || categories.fr.coastal} 
          fish={coastalFish} 
        />
        <FishSection 
          title={categories[language]?.pelagic || categories.fr.pelagic} 
          fish={pelagicFish} 
        />
        <FishSection 
          title={categories[language]?.other || categories.fr.other} 
          fish={otherFish} 
        />
      </div>
      </div>
    </div>
  );
};

export default FishSpeciesPage;
